// components/user/lead/ViewDetailsModal.jsx
import { Modal, Tag, Progress } from "antd";
import {
  RocketOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  ShoppingOutlined,
  WalletOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { formatPrice } from "../../../utils/priceFormatter";

const ViewDetailsModal = ({ visible, order, onClose, isDarkMode }) => {
  if (!order) return null;

  const data = order;

  const getStatusTag = (statusLabel, statusColor) => {
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>
        {statusLabel}
      </span>
    );
  };

  const InfoItem = ({ icon, label, value, badge }) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-700">
        {icon}
      </div>
      <div className="flex-1">
        <div
          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {label}
        </div>
        <div
          className={`text-sm font-medium flex items-center gap-2 flex-wrap ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
        >
          {value || "Not provided"}
          {badge}
        </div>
      </div>
    </div>
  );

  const progressPercentage =
    data.status_reports?.[0]?.progress_percentage || 10;
  const statusLabel = data.status_label || "Pending";
  const statusColor = data.status_color || "bg-blue-100 text-blue-700";

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-base font-semibold">
              Service Request Details
            </span>
          </div>
          <Tag
            color={data.is_express ? "orange" : "blue"}
            className="text-xs rounded-full"
          >
            {data.is_express ? (
              <>
                <ThunderboltOutlined className="mr-1" /> Express Pickup
              </>
            ) : (
              "Standard Pickup"
            )}
          </Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
      styles={{
        body: {
          padding: "16px 20px 20px 20px",
          maxHeight: "80vh",
          overflowY: "auto",
        },
        header: {
          padding: "16px 20px",
          marginBottom: 0,
          borderBottom: "1px solid #f0f0f0",
        },
        content: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      {/* Express Pickup Banner */}
      {data.is_express && (
        <div className="flex items-center gap-3 p-3 mb-4 border border-orange-200 rounded-xl bg-gradient-to-r from-orange-50 to-red-50 dark:from-gray-800 dark:border-gray-700">
          <RocketOutlined className="text-lg text-orange-500" />
          <div>
            <div className="text-sm font-semibold text-orange-600 dark:text-orange-400">
              Express Pickup Requested
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Pickup will be completed within 2 hours
            </div>
          </div>
        </div>
      )}

      {/* Progress Section */}
      <div className="pt-0 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Service Progress
          </span>
          <div className="flex items-center gap-2">
            {getStatusTag(statusLabel, statusColor)}
            <span className="text-xs font-semibold text-green-600 dark:text-green-400">
              {progressPercentage}%
            </span>
          </div>
        </div>
        <Progress
          percent={progressPercentage}
          strokeColor="#22c55e"
          showInfo={false}
          size="small"
        />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4">
        <div
          className={`p-3 rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Service No
          </div>
          <div className="text-xs font-mono font-semibold mt-0.5 dark:text-white">
            {data.service_no}
          </div>
        </div>
        <div
          className={`p-3 rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Created On
          </div>
          <div className="text-xs font-medium mt-0.5 dark:text-white">
            {dayjs(data.created_at).format("DD MMM YYYY")}
          </div>
        </div>
        <div
          className={`p-3 rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="text-xs text-gray-500 dark:text-gray-400">Amount</div>
          <div className="text-xs font-semibold text-green-600 dark:text-green-400 mt-0.5">
            {formatPrice(data.latestEvaluation?.evaluated_amount || 0)}
          </div>
        </div>
        <div
          className={`p-3 rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Last Updated
          </div>
          <div className="text-xs mt-0.5 dark:text-white">
            {dayjs(data.updated_at).format("DD MMM YYYY")}
          </div>
        </div>
      </div>

      {/* Two Column Grid */}
      <div className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
        {/* Pickup Details Card */}
        <div
          className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <EnvironmentOutlined className="text-sm text-red-500" />
            <h3 className="text-sm font-semibold dark:text-white">
              Pickup Details
            </h3>
          </div>
          <div className="px-3">
            <InfoItem
              icon={<HomeOutlined className="text-sm text-orange-500" />}
              label="Address"
              value={data.address_details?.address}
            />
            <InfoItem
              icon={<EnvironmentOutlined className="text-sm text-green-500" />}
              label="City / Pincode"
              value={`${data.address_details?.city_name} - ${data.address_details?.pincode}`}
            />
            <InfoItem
              icon={<HomeOutlined className="text-sm text-indigo-500" />}
              label="Address Type"
              value={data.address_details?.address_type}
            />
          </div>
        </div>

        {/* Product Details Card */}
        <div
          className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <ShoppingOutlined className="text-sm text-green-500" />
            <h3 className="text-sm font-semibold dark:text-white">
              Product Details
            </h3>
          </div>
          <div className="px-3">
            <InfoItem
              icon={<ShoppingOutlined className="text-sm text-green-500" />}
              label="Product Name"
              value={data.variation?.title}
            />
            <InfoItem
              icon={<WalletOutlined className="text-sm text-purple-500" />}
              label="Estimated Amount"
              value={formatPrice(data.latestEvaluation?.evaluated_amount || 0)}
            />
          </div>
        </div>
      </div>

      {/* Schedule Information */}
      {!data.is_express && data.scheduling && (
        <div
          className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"} mb-4`}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <ClockCircleOutlined className="text-sm text-blue-500" />
            <h3 className="text-sm font-semibold dark:text-white">
              Pickup Schedule
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-4 px-3">
            <InfoItem
              icon={<ClockCircleOutlined className="text-sm text-purple-500" />}
              label="Preferred Date"
              value={data.scheduling.spd}
            />
            <InfoItem
              icon={<ClockCircleOutlined className="text-sm text-blue-500" />}
              label="Time Slot"
              value={data.scheduling.slot}
            />
          </div>
        </div>
      )}

      {/* Documents */}
      {data.imei_numbers && data.imei_numbers.length > 0 && (
        <div
          className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white shadow-sm border border-gray-100"}`}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <FileTextOutlined className="text-sm text-indigo-500" />
            <h3 className="text-sm font-semibold dark:text-white">
              IMEI Numbers
            </h3>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {data.imei_numbers.map((imei, idx) => (
                <Tag key={idx} color="blue" className="font-mono text-xs">
                  {imei}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ViewDetailsModal;
